import React, { useState, useContext } from 'react';
import { useMutation } from '@apollo/react-hooks';

import { CredentialContext, credentialType } from '@providers';
import { useSnackbar } from '@src/providers/SnackbarProvider';

import { CREATE_OTP, VERIFY_OTP } from '@src/hooks/useOtp/queries';
import { createOtp } from '@organisms/SignupForm/__generated__/createOtp';
import { verifyOtp } from '@organisms/SignupForm/__generated__/verifyOtp';

import { MOBILE_NUMBER_REGEX, OTP_REGEX, WRONG_OTP_MESSAGE, CORRECT_OTP_MESSAGE } from '@src/utils/constants';

interface IProps {
  setStep?: React.Dispatch<React.SetStateAction<string>>;
  time?: any;
  startTimer?: any;
  isShowSnackbar?: boolean;
  setShowMessage?: React.Dispatch<React.SetStateAction<string>>;
}

const useOTPVerification = ({ setStep, time, startTimer, isShowSnackbar, setShowMessage }: IProps) => {
  const { showSnackbar } = useSnackbar();
  const { currentUser, setCurrentUser } = useContext<credentialType>(CredentialContext);

  const [otpRequested, setOtpRequested] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpId, setOtpId] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [errors, setErrors] = useState([]);

  const [createOtp] = useMutation<createOtp>(CREATE_OTP, {
    onCompleted: (data) => {
      if (data.createOtp.errors.length) {
        const errorArr = [...data.createOtp.errors].map((err) => err.message);
        const errorMsg = errorArr.join('\n');

        if (isShowSnackbar) {
          showSnackbar({ message: errorMsg });
        } else if (setShowMessage) {
          setShowMessage(errorMsg);
        }
        setErrors([...data.createOtp.errors]);
      } else {
        setOtpRequested(true);
        setStep('2');
        startTimer();
      }
    },
  });

  const handleRequestOtp = (rawMobileNumber: string) => {
    const mobileNumber = rawMobileNumber.trim();

    if (mobileNumber.length !== 10) {
      const wrongLengthMobileNumberMessage = 'Mobile number must be 10 digits long!';

      if (isShowSnackbar) {
        showSnackbar({ message: wrongLengthMobileNumberMessage });
      } else if (setShowMessage) {
        setShowMessage(wrongLengthMobileNumberMessage);
      }
    } else if (!MOBILE_NUMBER_REGEX.test(mobileNumber)) {
      const invalidMobileNumberMessage = 'Invalid mobile number.';

      if (isShowSnackbar) {
        showSnackbar({ message: invalidMobileNumberMessage });
      } else if (setShowMessage) {
        setShowMessage(invalidMobileNumberMessage);
      }
    } else {
      if (setShowMessage) {
        setShowMessage('');
      }
      createOtp({
        variables: { input: { receiver: mobileNumber } },
      });
    }
  };

  const [verifyOtp] = useMutation<verifyOtp>(VERIFY_OTP, {
    onCompleted: (data) => {
      if (data.verifyOtp.errors.length) {
        const errorArr = [...data.verifyOtp.errors].map((err) => err.message);
        const errorMsg = errorArr.join('\n');

        if (isShowSnackbar) {
          showSnackbar({ message: errorMsg });
        } else if (setShowMessage) {
          setShowMessage(errorMsg);
        }
        setErrors([...data.verifyOtp.errors]);
      } else {
        if (data.verifyOtp.verified) {
          setStep('3');
          setOtpVerified(data.verifyOtp.verified);
          setOtpId(data.verifyOtp.otp.id);
          setOtpValue(data.verifyOtp.otp.value);
          if (isShowSnackbar) {
            showSnackbar({ message: CORRECT_OTP_MESSAGE });
          } else if (setShowMessage) {
            setShowMessage(CORRECT_OTP_MESSAGE);
          }
        } else {
          if (isShowSnackbar) {
            showSnackbar({ message: WRONG_OTP_MESSAGE });
          } else if (setShowMessage) {
            setShowMessage(WRONG_OTP_MESSAGE);
          }
        }
      }
    },
  });

  const handleVerifyOtp = (otp: string, mobileNumber: string, onSuccess?: () => void) => {
    const otpNumber = otp.trim();

    if (!OTP_REGEX.test(otpNumber)) {
      const wrongLengthOTPNumberMessage = 'OTP number must be 6 digits long number!';
      if (isShowSnackbar) {
        showSnackbar({ message: wrongLengthOTPNumberMessage });
      } else if (setShowMessage) {
        setShowMessage(wrongLengthOTPNumberMessage);
      }
    } else {
      verifyOtp({
        variables: {
          input: {
            receiver: mobileNumber,
            value: otp,
          },
        },
      }).then((data) => {
        if (data?.data?.verifyOtp?.verified) {
          const copyCurrentUser = JSON.parse(JSON.stringify(currentUser));
          copyCurrentUser.user.mobileNumber = Number(mobileNumber);
          copyCurrentUser.user.mobileVerified = data?.data?.verifyOtp?.verified;

          // local user info update
          setCurrentUser(copyCurrentUser);

          if (onSuccess) {
            onSuccess();
          }
        }
      });
    }
  };

  return {
    otpRequested,
    setOtpRequested,
    otpVerified,
    setOtpVerified,
    otpId,
    otpValue,
    errors,
    handleRequestOtp,
    handleVerifyOtp,
  };
};

export default useOTPVerification;
